import type { ClientEvent, PresenceStatus, ServerEvent } from '@callzone/shared';
import { useStore } from '../store';
import { api, gatewayUrl } from './api';
import { voice } from './voice';
import { notifyMessage } from './platform';

/**
 * Conexao permanente com o servidor: chat, presenca e estado de voz chegam por
 * aqui. Reconecta sozinha com backoff, porque um app de call precisa aguentar
 * a internet cair por alguns segundos sem obrigar a pessoa a reabrir tudo.
 */
class Gateway {
  private socket: WebSocket | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private token: string | null = null;
  private closedByUs = false;

  connect(token: string) {
    this.token = token;
    this.closedByUs = false;
    this.open();
  }

  private open() {
    const token = this.token;
    if (!token) return;
    this.cleanupSocket();

    const socket = new WebSocket(gatewayUrl());
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempts = 0;
      socket.send(JSON.stringify({ op: 'identify', token } satisfies ClientEvent));
    });

    socket.addEventListener('message', (event) => {
      let payload: ServerEvent;
      try {
        payload = JSON.parse(String(event.data));
      } catch {
        return;
      }
      this.handle(payload);
    });

    socket.addEventListener('close', () => {
      this.stopHeartbeat();
      if (this.closedByUs) return;
      useStore.getState().setConnectionError('Conexao perdida. Reconectando...');
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // O evento 'close' vem logo depois e cuida da reconexao.
    });
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    // 1s, 2s, 4s... ate 15s.
    const delay = Math.min(15_000, 1000 * 2 ** this.attempts);
    this.attempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private startHeartbeat(intervalMs: number) {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => this.send({ op: 'heartbeat' }), intervalMs);
  }

  private stopHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private cleanupSocket() {
    if (!this.socket) return;
    this.socket.onclose = null;
    if (this.socket.readyState === WebSocket.OPEN) this.socket.close();
    this.socket = null;
  }

  private handle(event: ServerEvent) {
    const store = useStore.getState();

    switch (event.op) {
      case 'hello':
        this.startHeartbeat(event.heartbeatIntervalMs);
        break;

      case 'ready':
        store.applyReady(event);
        // Apelidos, amizades e conversas nao vem no `ready`: nao pertencem a
        // guild nenhuma. Falhar aqui degrada a tela, nao derruba a sessao.
        void api
          .aliases()
          .then(({ aliases }) => useStore.getState().setAliases(aliases))
          .catch(() => undefined);
        void api
          .amizades()
          .then(({ amizades }) => useStore.getState().setAmizades(amizades))
          .catch(() => undefined);
        void api
          .conversas()
          .then(({ conversas }) => useStore.getState().setConversas(conversas))
          .catch(() => undefined);
        break;

      case 'user_update': {
        // Se for voce, atualiza tambem o `me` — senao a barra de baixo
        // continuaria com o nome e a foto antigos.
        if (event.user.id === useStore.getState().me?.id) store.setMe(event.user);
        store.upsertUser(event.user);
        break;
      }

      case 'amizades_update': {
        store.setAmizades(event.amizades);
        // Desfazer amizade fecha a conversa: a lista precisa acompanhar.
        void api
          .conversas()
          .then(({ conversas }) => useStore.getState().setConversas(conversas))
          .catch(() => undefined);
        break;
      }

      case 'dm_message_create': {
        store.upsertDmMessage(event.message);
        const estado = useStore.getState();
        if (event.message.authorId !== estado.me?.id && estado.activeDmId !== event.message.channelId) {
          const conversa = estado.conversas.find((c) => c.id === event.message.channelId);
          notifyMessage(conversa?.user.nickname ?? 'Mensagem privada', event.message.content);
        }
        break;
      }

      case 'dm_call_ring':
        // So toca se voce nao estiver ja dentro dessa mesma ligacao.
        if (useStore.getState().voice.channelId !== event.channelId) {
          store.setChamando({ channelId: event.channelId, fromUserId: event.fromUserId });
        }
        break;

      case 'message_create': {
        store.upsertMessage(event.message);
        const state = useStore.getState();
        // Avisa apenas quando a mensagem e de outra pessoa.
        if (event.message.authorId !== state.me?.id) {
          const guild = state.activeGuildId ? state.guilds[state.activeGuildId] : undefined;
          const author =
            guild?.members.find((m) => m.userId === event.message.authorId)?.user.nickname ??
            'Nova mensagem';
          notifyMessage(author, event.message.content);
        }
        break;
      }

      case 'message_update':
        store.upsertMessage(event.message);
        break;

      case 'message_delete':
        store.removeMessage(event.channelId, event.messageId);
        break;

      case 'typing_start':
        if (event.userId !== store.me?.id) store.markTyping(event.channelId, event.userId);
        break;

      case 'presence_update':
        store.setPresence(event.presence.userId, event.presence.status);
        break;

      case 'voice_state_update': {
        store.setVoiceState(event.state);
        const self = useStore.getState();
        // Um moderador pode ter movido ou desconectado voce: o cliente obedece.
        if (event.state.userId === self.me?.id) {
          if (event.state.channelId === null && self.voice.channelId) {
            void voice.leave(false);
          } else if (
            event.state.channelId &&
            self.voice.channelId &&
            event.state.channelId !== self.voice.channelId
          ) {
            void voice.join(event.state.guildId, event.state.channelId);
          }
        }
        break;
      }

      case 'channel_create':
      case 'channel_update':
        store.upsertChannel(event.channel);
        break;

      case 'channel_delete':
        store.removeChannel(event.guildId, event.channelId);
        break;

      case 'role_create':
      case 'role_update':
        store.upsertRole(event.role);
        break;

      case 'role_delete':
        store.removeRole(event.guildId, event.roleId);
        break;

      case 'member_add':
      case 'member_update':
        store.upsertMember(event.member);
        break;

      case 'member_remove':
        store.removeMember(event.guildId, event.userId);
        break;

      case 'guild_update':
        store.updateGuild(event.guild);
        break;

      case 'force_disconnect':
        this.closedByUs = true;
        store.toast(event.reason, 'error');
        void voice.leave(false);
        break;

      case 'error':
        store.toast(event.message, 'error');
        break;
    }
  }

  send(event: ClientEvent) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(event));
    }
  }

  setStatus(status: PresenceStatus) {
    this.send({ op: 'presence_update', status });
  }

  startTyping(channelId: string) {
    this.send({ op: 'typing_start', channelId });
  }

  updateVoiceState(
    guildId: string,
    channelId: string | null,
    flags: { selfMute?: boolean; selfDeaf?: boolean; streaming?: boolean; camera?: boolean } = {},
  ) {
    this.send({ op: 'voice_state_update', guildId, channelId, ...flags });
  }

  disconnect() {
    this.closedByUs = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.cleanupSocket();
  }
}

export const gateway = new Gateway();
